import { useRef, useState, useEffect } from "react";
import { SplitReveal, FadeUp } from "../components/SplitReveal";
import { useLanguage } from "../lib/i18n";
import { IMAGES } from "../lib/images";

// SQUEEGEE — "İmleç Sileceği": reveal glossy layer under cursor via radial mask.
export function Squeegee() {
  const { t } = useLanguage();
  const stageRef = useRef(null);
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const [inside, setInside] = useState(false);

  const update = (clientX, clientY) => {
    const rect = stageRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPos({
      x: ((clientX - rect.left) / rect.width) * 100,
      y: ((clientY - rect.top) / rect.height) * 100,
    });
  };

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const onTouch = (e) => {
      const tch = e.touches[0];
      if (tch) update(tch.clientX, tch.clientY);
    };
    el.addEventListener("touchmove", onTouch, { passive: true });
    return () => el.removeEventListener("touchmove", onTouch);
  }, []);

  const mask = `radial-gradient(circle 130px at ${pos.x}% ${pos.y}%, #000 0%, #000 60%, transparent 100%)`;

  return (
    <section className="relative bg-[#08090B] px-6 py-24 md:px-12 md:py-40" data-testid="squeegee-section">
      <div className="mx-auto grid max-w-[1600px] items-center gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <FadeUp>
            <p className="heading mb-4 text-sm not-italic tracking-[0.3em] text-hars-orange">{t.squeegee.kicker}</p>
          </FadeUp>
          <SplitReveal text={t.squeegee.title} className="heading text-5xl text-white md:text-6xl" />
          <FadeUp delay={0.1}>
            <p className="mt-6 font-inter text-base text-hars-muted md:text-lg">{t.squeegee.text}</p>
            <p className="mt-4 font-inter text-sm text-hars-orangeLight">
              <span className="hidden md:inline">{t.squeegee.hint}</span>
              <span className="md:hidden">{t.squeegee.hintTouch}</span>
            </p>
          </FadeUp>
        </div>

        <div className="lg:col-span-8">
          <div
            ref={stageRef}
            data-cursor
            data-cursor-label="SİL"
            data-testid="squeegee-stage"
            onMouseMove={(e) => update(e.clientX, e.clientY)}
            onMouseEnter={() => setInside(true)}
            onMouseLeave={() => setInside(false)}
            className="relative aspect-[16/10] w-full overflow-hidden rounded-2xl border border-white/5"
          >
            {/* Dull / scratched base */}
            <img
              src={IMAGES.hoodMacro}
              alt="Korumasız mat boya yüzeyi"
              className="absolute inset-0 h-full w-full object-cover"
              style={{ filter: "grayscale(0.6) brightness(0.55) contrast(0.9) blur(1px)" }}
            />
            <div className="absolute inset-0 bg-black/20" />
            {/* Glossy revealed layer */}
            <img
              src={IMAGES.hoodMacro}
              alt="HARSPRO ile parlak korunmuş yüzey"
              className="absolute inset-0 h-full w-full object-cover"
              style={{
                filter: "brightness(1.15) contrast(1.2) saturate(1.15)",
                WebkitMaskImage: mask,
                maskImage: mask,
              }}
            />
            {/* soft ring at cursor */}
            {inside && (
              <div
                className="pointer-events-none absolute h-[260px] w-[260px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-hars-orange/30"
                style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                aria-hidden="true"
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
